import { filterNodes, groupModels, makeFromVin, modelFamily, typeMatchOutcome, vehicleEngineCodes, type ModelFamily } from "@szerviz/core";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp, useFeature } from "../context";
import type { Vehicle, VehicleTypeMatch, VehicleTypeNode } from "../data";
import { ErrorText } from "./ErrorText";
import { photoBase64 } from "./VehiclePhoto";

type Mode = "list" | "registration";
/** Longest list shown at once; typing narrows it. */
const SHOWN = 40;

const key = (s: string | null | undefined) => (s ?? "").trim().replace(/\s+/g, " ").toUpperCase();

/**
 * Puts the own vehicle into the shared type tree (owner's idea, 2026-11-02), two ways as cards:
 * from the list (make → model → generation → engine variant, stopping at a model is allowed),
 * or from the registration certificate (the data read from it, the VIN's maker): one clear match
 * is asked "Ez az?", several are listed, none falls back to the list. Nothing is invented.
 */
export function TypePicker({ vehicle, start, onChosen }: {
  vehicle: Vehicle;
  /** Continue from a node already chosen (a vehicle placed down to its model: the engine step). */
  start?: VehicleTypeNode[];
  onChosen: (typeId: string) => void;
}) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const vinMake = makeFromVin(vehicle.vin);
  const hasRegData = !!(vehicle.make?.trim() || vinMake);
  const [mode, setMode] = useState<Mode>(start ? "list" : hasRegData ? "registration" : "list");
  const [noMatch, setNoMatch] = useState(false);
  const toList = (none: boolean) => {
    setNoMatch(none);
    setMode("list");
  };
  return (
    <div className="type-picker">
      {!start && (
        <fieldset className="choice-cards">
          <legend>{t("typePicker.how")}</legend>
          {(["list", "registration"] as const).map((m) => (
            <label key={m} className={`choice-card${mode === m ? " selected" : ""}`}>
              <input type="radio" name="typePickerMode" checked={mode === m} onChange={() => { setNoMatch(false); setMode(m); }} />
              <span>
                <strong>{t(`typePicker.mode.${m}`)}</strong>
                <br />
                <small className="muted">{t(`typePicker.mode.${m}Note`)}</small>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {mode === "list" ? (
        <>
          {noMatch && <p className="warn-box">{t("typePicker.noMatch")}</p>}
          <ListPicker vehicle={vehicle} start={start} onChosen={onChosen} />
        </>
      ) : (
        <RegistrationPicker vehicle={vehicle} workshopId={workshop.id} store={store} onChosen={onChosen} onNone={() => toList(true)} onOther={() => toList(false)} />
      )}
    </div>
  );
}

function ListPicker({ vehicle, start, onChosen }: { vehicle: Vehicle; start?: VehicleTypeNode[]; onChosen: (typeId: string) => void }) {
  const { t } = useTranslation();
  const { store } = useApp();
  const [makes, setMakes] = useState<VehicleTypeNode[] | null>(null);
  const [make, setMake] = useState<VehicleTypeNode | null>(start?.find((n) => n.level === "make") ?? null);
  const [models, setModels] = useState<VehicleTypeNode[]>([]);
  const [family, setFamily] = useState<ModelFamily<VehicleTypeNode> | null>(null);
  const [model, setModel] = useState<VehicleTypeNode | null>(start?.find((n) => n.level === "model") ?? null);
  const [engines, setEngines] = useState<VehicleTypeNode[] | null>(null);
  const [suggested, setSuggested] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    void store.vehicleTypeChildren(null).then(setMakes, setError);
  }, [store]);
  useEffect(() => {
    setModels([]);
    if (make) void store.vehicleTypeChildren(make.id).then(setModels, setError);
  }, [store, make]);
  useEffect(() => {
    setEngines(null);
    if (!model) return;
    void store.vehicleTypeChildren(model.id).then(setEngines, setError);
    // the engine variants that fit the vehicle's own data come first
    void store.vehicleTypeMatch(vehicle.id, {}).then((m) => setSuggested(m.filter((x) => x.level === "engine").map((x) => x.id)), () => setSuggested([]));
  }, [store, model, vehicle.id]);
  useEffect(() => setQuery(""), [make, family, model]);

  const families = useMemo(() => groupModels(models), [models]);
  const makeHint = key(vehicle.make) || key(makeFromVin(vehicle.vin));
  const familyHint = vehicle.model ? modelFamily(vehicle.model) : "";
  const codes = vehicleEngineCodes(vehicle.engineCode);

  const step: "make" | "model" | "generation" | "engine" = !make ? "make" : model ? "engine" : family && family.nodes.length > 1 ? "generation" : "model";
  const pickFamily = (f: ModelFamily<VehicleTypeNode>) => {
    if (f.nodes.length === 1) setModel(f.nodes[0]!);
    else setFamily(f);
  };
  const crumbs: { label: string; name: string; clear: () => void }[] = [];
  if (make) crumbs.push({ label: t("typePicker.step.make"), name: make.name, clear: () => { setMake(null); setFamily(null); setModel(null); } });
  if (family && family.nodes.length > 1) crumbs.push({ label: t("typePicker.step.model"), name: family.label, clear: () => { setFamily(null); setModel(null); } });
  if (model) crumbs.push({ label: t(family && family.nodes.length > 1 ? "typePicker.step.generation" : "typePicker.step.model"), name: model.name, clear: () => setModel(null) });

  // the list of the current step, the suggested ones first
  type Row = { id: string; name: string; hint: boolean; pick: () => void };
  let rows: Row[] = [];
  if (step === "make") rows = (makes ?? []).map((n) => ({ id: n.id, name: n.name, hint: key(n.name) === makeHint, pick: () => setMake(n) }));
  if (step === "model") rows = families.map((f) => ({ id: f.family, name: f.label, hint: f.family === familyHint, pick: () => pickFamily(f) }));
  if (step === "generation") rows = family!.nodes.map((n) => ({ id: n.id, name: n.name, hint: key(n.name) === key(vehicle.model), pick: () => setModel(n) }));
  if (step === "engine") rows = (engines ?? []).map((n) => ({ id: n.id, name: n.name, hint: suggested.includes(n.id) || codes.some((c) => key(n.name).replace(/\s/g, "").includes(c)), pick: () => onChosen(n.id) }));
  const filtered = filterNodes(rows, query).sort((a, b) => Number(b.hint) - Number(a.hint));

  return (
    <div className="list-picker">
      <ErrorText error={error} />
      {crumbs.length > 0 && (
        <ol className="type-crumbs">
          {crumbs.map((c) => (
            <li key={c.label}>
              <small className="muted">{c.label}</small> <strong>{c.name}</strong>{" "}
              <button type="button" className="link" onClick={c.clear}>{t("workOrder.change")}</button>
            </li>
          ))}
        </ol>
      )}
      <label className="field">
        <span>{t(`typePicker.step.${step}`)}</span>
        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("typePicker.search")} aria-label={t(`typePicker.step.${step}`)} />
      </label>
      {makes !== null && rows.length === 0 && <p className="muted">{t(step === "engine" ? "typePicker.noEngines" : "typePicker.empty")}</p>}
      {/* a search that finds nothing says so, with a way back to the whole list (#151) */}
      {rows.length > 0 && filtered.length === 0 && (
        <div className="actions">
          <p className="muted" role="status">{t("typePicker.noResults", { query: query.trim() })}</p>
          <button type="button" onClick={() => setQuery("")}>{t("typePicker.clearSearch")}</button>
        </div>
      )}
      <ul className="pick-list">
        {filtered.slice(0, SHOWN).map((r) => (
          <li key={r.id}>
            <button type="button" className={r.hint ? "suggested" : undefined} onClick={r.pick}>
              {r.name}
              {r.hint && <span className="badge ok">{t("typePicker.suggested")}</span>}
            </button>
          </li>
        ))}
      </ul>
      {filtered.length > SHOWN && <p className="muted small">{t("typePicker.more", { n: filtered.length - SHOWN })}</p>}
      {model && (
        <div className="actions">
          <button type="button" onClick={() => onChosen(model.id)}>{t("typePicker.stopHere", { name: model.name })}</button>
          <span className="muted small">{t("typePicker.stopNote")}</span>
        </div>
      )}
    </div>
  );
}

function RegistrationPicker({ vehicle, workshopId, store, onChosen, onNone, onOther }: {
  vehicle: Vehicle;
  workshopId: string;
  store: ReturnType<typeof useApp>["store"];
  onChosen: (typeId: string) => void;
  onNone: () => void;
  onOther: () => void;
}) {
  const { t } = useTranslation();
  const photoOn = useFeature("aiVehiclePhoto");
  const [ai, setAi] = useState(false);
  const [matches, setMatches] = useState<VehicleTypeMatch[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const vinMake = makeFromVin(vehicle.vin);

  useEffect(() => {
    if (photoOn) void store.aiAvailable().then(setAi);
  }, [photoOn, store]);
  // the vehicle's own data, which came from the registration certificate (or the VIN's maker)
  useEffect(() => {
    if (!vehicle.make?.trim() && !vinMake) return setMatches([]);
    void store.vehicleTypeMatch(vehicle.id, { make: vehicle.make?.trim() || vinMake }).then(setMatches, setError);
  }, [store, vehicle.id, vehicle.make, vinMake]);

  const read = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const img = await photoBase64(file);
      const r = await store.readVehiclePhoto(workshopId, img.data, img.mediaType);
      const f = r.fields;
      const make = String(f.make ?? "").trim() || makeFromVin(String(f.vin ?? "")) || vehicle.make?.trim() || vinMake;
      const kw = Number(f.powerKw);
      const found = make ? await store.vehicleTypeMatch(vehicle.id, { make, model: f.model ? String(f.model) : null, engineCode: f.engineCode ? String(f.engineCode) : null, powerKw: Number.isFinite(kw) && kw > 0 ? kw : null }) : [];
      // nothing fits what was read: on to the list (never a made up type)
      if (found.length === 0) onNone();
      else setMatches(found);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const outcome = matches ? typeMatchOutcome(matches) : null;
  useEffect(() => {
    if (outcome?.kind === "several") setChosen(outcome.list[0]!.id);
  }, [matches]);
  const label = (m: VehicleTypeMatch) => (
    <>
      <strong>{m.make} {m.model}</strong>
      <br />
      {m.engine ?? t("typePicker.modelOnly")}
    </>
  );

  return (
    <div className="registration-picker">
      {photoOn && ai && (
        <label className="button photo-button">
          {busy ? t("vehiclePhoto.reading") : t("typePicker.readPhoto")}
          <input type="file" accept="image/*,application/pdf" hidden disabled={busy} onChange={(e) => void read(e.target.files?.[0])} aria-label={t("typePicker.readPhoto")} />
        </label>
      )}
      <ErrorText error={error} />
      {outcome?.kind === "one" && (
        <div className="type-match" role="status">
          <p className="type-match-q">{t("typePicker.isThis")}</p>
          <p>{label(outcome.best)}</p>
          <div className="actions">
            <button type="button" className="primary" onClick={() => onChosen(outcome.best.id)}>{t("typePicker.yes")}</button>
            <button type="button" onClick={onOther}>{t("typePicker.no")}</button>
          </div>
        </div>
      )}
      {outcome?.kind === "several" && (
        <>
          <fieldset className="choice-cards">
            <legend>{t("dataSheet.pickType")}</legend>
            {outcome.list.map((m) => (
              <label key={m.id} className={`choice-card${chosen === m.id ? " selected" : ""}`}>
                <input type="radio" name="vehicleType" checked={chosen === m.id} onChange={() => setChosen(m.id)} />
                <span>{label(m)}</span>
              </label>
            ))}
          </fieldset>
          <div className="actions">
            <button type="button" className="primary" disabled={!chosen} onClick={() => onChosen(chosen!)}>{t("dataSheet.useType")}</button>
            <button type="button" onClick={onOther}>{t("typePicker.noneOfThese")}</button>
          </div>
        </>
      )}
      {outcome?.kind === "none" && (
        <div className="actions">
          <p className="muted">{t(vehicle.make?.trim() || vinMake ? "typePicker.noMatchShort" : "typePicker.noRegData")}</p>
          <button type="button" className="primary" onClick={onNone}>{t("typePicker.toList")}</button>
        </div>
      )}
    </div>
  );
}
